import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

const BottomNav = () => {
  const [categories, setCategories] = useState([]);
  const [openCategory, setOpenCategory] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSubCategories = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/sub-categories/get-all-sub-categories`
        );
        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();

        const groupedByCategory = data.reduce((acc, subCategory) => {
          const categoryId = subCategory.category._id;
          const categoryName = subCategory.category.name;

          if (!acc[categoryId]) {
            acc[categoryId] = {
              id: categoryId,
              name: categoryName,
              subCategories: [],
            };
          }

          acc[categoryId].subCategories.push(subCategory);
          return acc;
        }, {});

        setCategories(Object.values(groupedByCategory));
        setIsLoading(false);
      } catch (err) {
        setError("Failed to fetch categories");
        setIsLoading(false);
        console.error(err);
      }
    };

    fetchSubCategories();
  }, []);

  const handleSubCategoryClick = (categoryId, subCategoryId) => {
    setIsSidebarOpen(false);
    navigate(`/allcategories/${categoryId}`);
  };

  if (isLoading) {
    return (
      <React.Fragment>
        {/* Mobile Skeleton */}
        <div className="xl:hidden bg-white shadow-sm p-4">
          <div className="animate-pulse space-y-3">
            {[...Array(1)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded w-2/3"></div>
            ))}
          </div>
        </div>

        {/* Desktop Skeleton */}
        <nav className="hidden xl:block bg-white shadow-sm">
          <div className="mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="animate-pulse flex space-x-6 justify-center">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-5 w-28 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </nav>
      </React.Fragment>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-white p-4 text-center text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div className="bg-white border-b border-gray-100 shadow-sm hidden sm:block">
      {/* Mobile Toggle Button */}
      <div className="xl:hidden flex justify-between items-center mx-auto px-4 py-3">
        <span className="font-medium text-gray-800">Browse Categories</span>
        <button
          className="text-gray-600 hover:text-gray-900"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          aria-label="Toggle categories"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
            />
          </svg>
        </button>
      </div>

      {/* Sidebar (Mobile) */}
      <div
        className={`fixed inset-0 z-50 overflow-hidden ${
          isSidebarOpen ? "block" : "hidden"
        }`}
      >
        <div
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
        <div
          className={`absolute left-0 top-0 h-full w-64 bg-white shadow-lg transform ${
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          } transition-transform duration-300 ease-in-out`}
        >
          <div className="flex justify-between items-center p-4 border-b border-gray-200">
            <span className="font-medium text-gray-800">Categories</span>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close menu"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="overflow-y-auto h-[calc(100%-60px)]">
            <ul>
              {categories.map((category) => (
                <li key={category.id} className="border-b border-gray-100">
                  <button
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex justify-between items-center"
                    onClick={() =>
                      setOpenCategory(
                        openCategory === category.id ? null : category.id
                      )
                    }
                  >
                    <span className="text-gray-700 text-sm font-light capitalize">
                      {category.name}
                    </span>
                    <svg
                      className={`w-4 h-4 text-gray-500 transform transition-transform ${
                        openCategory === category.id ? "rotate-180" : ""
                      }`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {openCategory === category.id && (
                    <ul className="pl-4 bg-gray-50">
                      {category.subCategories.map((subCategory) => (
                        <li key={subCategory._id}>
                          <button
                            onClick={() =>
                              handleSubCategoryClick(
                                category.id,
                                subCategory._id
                              )
                            }
                            className="block text-sm font-light capitalize w-full text-left px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-gray-100"
                          >
                            {subCategory.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Desktop Nav */}
      <nav className="hidden xl:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ul className="flex justify-center space-x-1">
            {categories.map((category) => (
              <li
                key={category.id}
                className="relative"
                onMouseEnter={() => setOpenCategory(category.id)}
                onMouseLeave={() => setOpenCategory(null)}
              >
                <button className="capitalize px-4 py-3 text-gray-700 hover:text-blue-600 font-light text-sm flex items-center space-x-1 transition-colors duration-200">
                  <span>{category.name}</span>
                  <svg
                    className={`w-4 h-4 text-gray-500 transform transition-transform ${
                      openCategory === category.id ? "rotate-180" : ""
                    }`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {openCategory === category.id && (
                  <div className="absolute left-1/2 transform -translate-x-1/2 z-10 w-56">
                    <div className="mt-2 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
                      <ul>
                        {category.subCategories.map((subCategory) => (
                          <li
                            key={subCategory._id}
                            className="border-b border-gray-100 last:border-0"
                          >
                            <button
                              onClick={() =>
                                handleSubCategoryClick(
                                  category.id,
                                  subCategory._id
                                )
                              }
                              className="px-4 py-2 text-gray-700 hover:bg-gray-50 w-full text-left flex items-center"
                            >
                              {subCategory.imgUrl && (
                                <div className="flex-shrink-0 h-6 w-6 rounded-md overflow-hidden bg-gray-100 mr-3">
                                  <img
                                    src={subCategory.imgUrl}
                                    alt={subCategory.name}
                                    className="h-full w-full object-cover"
                                  />
                                </div>
                              )}
                              <span className="truncate text-sm font-light capitalize">
                                {subCategory.name}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </div>
  );
};

export default BottomNav;

// import React, { useState, useEffect } from "react";
// import { Link, useNavigate } from "react-router-dom";

// const BottomNav = () => {
//   const [categories, setCategories] = useState([]);
//   const [openCategory, setOpenCategory] = useState(null);
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [isLoading, setIsLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const navigate = useNavigate();

//   useEffect(() => {
//     const fetchSubCategories = async () => {
//       try {
//         setIsLoading(true);
//         const response = await fetch(
//           `${process.env.REACT_APP_API_URL}/sub-categories/get-all-sub-categories`
//         );
//         if (!response.ok) throw new Error("Network response was not ok");
//         const data = await response.json();

//         const groupedByCategory = data.reduce((acc, subCategory) => {
//           const categoryId = subCategory.category._id;
//           const categoryName = subCategory.category.name;

//           if (!acc[categoryId]) {
//             acc[categoryId] = {
//               id: categoryId,
//               name: categoryName,
//               subCategories: [],
//             };
//           }

//           acc[categoryId].subCategories.push(subCategory);
//           return acc;
//         }, {});

//         setCategories(Object.values(groupedByCategory));
//         setIsLoading(false);
//       } catch (err) {
//         setError("Failed to fetch categories");
//         setIsLoading(false);
//         console.error(err);
//       }
//     };

//     fetchSubCategories();
//   }, []);

//   const handleSubCategoryClick = (categoryId, subCategoryId) => {
//     setIsSidebarOpen(false);

//     navigate(`/allcategories/${categoryId}`);
//     // window.scrollTo({ top: 0, behavior: "smooth" });
//   };

//   if (isLoading) {
//     return (
//       <React.Fragment>
//         {/* Mobile Skeleton */}
//         <div className="xl:hidden bg-white shadow-md p-4">
//           <div className="animate-pulse space-y-3">
//             {[...Array(1)].map((_, i) => (
//               <div key={i} className="h-4 bg-slate-200 rounded w-2/3"></div>
//             ))}
//           </div>
//         </div>

//         {/* Desktop Skeleton */}
//         <nav className="hidden xl:block bg-white shadow-md">
//           <div className="mx-auto px-4 sm:px-6 lg:px-8 py-4">
//             <div className="animate-pulse flex space-x-6 justify-center">
//               {[...Array(8)].map((_, i) => (
//                 <div key={i} className="h-5 w-28 bg-slate-200 rounded"></div>
//               ))}
//             </div>
//           </div>
//         </nav>
//       </React.Fragment>
//     );
//   }

//   if (error) {
//     return (
//       <div className="w-full bg-white p-4 text-center text-red-500">
//         {error}
//       </div>
//     );
//   }

//   return (
//     <div className="hidden sm:block">
//       {/* Mobile Toggle Button */}
//       <div className="xl:hidden bg-white shadow-md flex justify-between items-center mx-auto px-3 sm:px-6 lg:px-8 py-4">
//         <span className="font-semibold text-lg">Categories</span>
//         <button
//           className="text-slate-800"
//           onClick={() => setIsSidebarOpen(!isSidebarOpen)}
//         >
//           <svg
//             className="w-6 h-6"
//             fill="none"
//             stroke="currentColor"
//             strokeWidth="2"
//             viewBox="0 0 24 24"
//           >
//             <path
//               strokeLinecap="round"
//               strokeLinejoin="round"
//               d="M4 6h16M4 12h16M4 18h16"
//             />
//           </svg>
//         </button>
//       </div>

//       {/* Sidebar (Mobile) */}
//       <div
//         className={`fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50 transform mx-auto ${
//           isSidebarOpen ? "translate-x-0" : "-translate-x-full"
//         } transition-transform duration-300 xl:hidden`}
//       >
//         <div className="flex justify-between items-center p-4 border-b">
//           <span className="font-semibold text-lg">Categories</span>
//           <button
//             onClick={() => setIsSidebarOpen(false)}
//             className="text-slate-600"
//           >
//             <svg
//               className="w-5 h-5"
//               fill="none"
//               stroke="currentColor"
//               strokeWidth="2"
//               viewBox="0 0 24 24"
//             >
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 d="M6 18L18 6M6 6l12 12"
//               />
//             </svg>
//           </button>
//         </div>
//         <ul className="overflow-y-auto h-[calc(100%-60px)]">
//           {categories.map((category) => (
//             <li key={category.id}>
//               <button
//                 className="w-full text-left px-4 py-3 border-b hover:bg-slate-50"
//                 onClick={() =>
//                   setOpenCategory(
//                     openCategory === category.id ? null : category.id
//                   )
//                 }
//               >
//                 <div className="flex justify-between items-center text-slate-800">
//                   <span>{category.name}</span>
//                   <svg className="w-4 h-4" viewBox="0 0 12 12">
//                     <path d="M10 2.586 11.414 4 6 9.414.586 4 2 2.586l4 4z" />
//                   </svg>
//                 </div>
//               </button>
//               {openCategory === category.id && (
//                 <ul className="pl-6 pb-2">
//                   {category.subCategories.map((subCategory) => (
//                     <li key={subCategory._id}>
//                       <button
//                         onClick={() =>
//                           handleSubCategoryClick(category.id, subCategory._id)
//                         }
//                         className="block w-full text-left px-2 py-1 text-slate-700 hover:text-indigo-600"
//                       >
//                         {subCategory.name}
//                       </button>
//                     </li>
//                   ))}
//                 </ul>
//               )}
//             </li>
//           ))}
//         </ul>
//       </div>

//       {/* Desktop Nav */}
//       <nav className="hidden xl:block bg-slate-200 shadow-md">
//         <div className="mx-auto px-3 sm:px-6 lg:px-8">
//           <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-x-4 gap-y-2 justify-items-center text-sm font-medium">
//             {categories.map((category) => (
//               <li
//                 key={category.id}
//                 className="relative p-4"
//                 onMouseEnter={() => setOpenCategory(category.id)}
//                 onMouseLeave={() => setOpenCategory(null)}
//               >
//                 <div className="flex items-center space-x-1 cursor-pointer text-slate-800 hover:text-slate-900">
//                   <span>{category.name}</span>
//                   <svg className="w-3 h-3 fill-slate-500" viewBox="0 0 12 12">
//                     <path d="M10 2.586 11.414 4 6 9.414.586 4 2 2.586l4 4z" />
//                   </svg>
//                 </div>

//                 {openCategory === category.id && (
//                   <ul className="z-10 origin-top absolute top-full left-1/2 -translate-x-1/2 min-w-[240px] bg-white border border-slate-200 p-2 rounded-lg shadow-xl">
//                     {category.subCategories.map((subCategory) => (
//                       <li key={subCategory._id}>
//                         <button
//                           onClick={() =>
//                             handleSubCategoryClick(category.id, subCategory._id)
//                           }
//                           className="text-slate-800 hover:bg-slate-50 flex items-center p-2 w-full text-left"
//                         >
//                           <div className="flex items-center justify-center bg-white border border-slate-200 rounded shadow-sm h-7 w-7 shrink-0 mr-3">
//                             <img
//                               src={subCategory.imgUrl}
//                               alt={subCategory.name}
//                               className="w-full h-full"
//                             />
//                           </div>
//                           <span className="whitespace-nowrap">
//                             {subCategory.name}
//                           </span>
//                         </button>
//                       </li>
//                     ))}
//                   </ul>
//                 )}
//               </li>
//             ))}
//           </ul>
//         </div>
//       </nav>
//     </div>
//   );
// };

// export default BottomNav;
